/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';

import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { CustomerFormModel } from '../models/customer-form.model';
import { CustomerFormService } from '../services/customer-form.service';

@Component({
	selector: 'customer-form',
	templateUrl: './customer-form.component.html',
	styleUrls: ['./customer-form.component.scss'],
	standalone: false,
})
export class CustomerFormComponent extends CreateUpdateBaseComponent<CustomerFormModel> implements OnInit {
	asideClass = 'aside-content aside-width-10';

	constructor(protected fb: FormBuilder, private service: CustomerFormService, protected asideService: AsideService, protected formHelperService: ReactiveFormHelperService, protected confirmationDialogService: ConfirmationDialogService) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.updateAsideClass();
		window.addEventListener('resize', this.updateAsideClass.bind(this));
	}

	ngOnDestroy(): void {
		window.removeEventListener('resize', this.updateAsideClass.bind(this));
	}

	updateAsideClass() {
		if (window.innerWidth <= 1200) {
			this.asideClass = 'aside-content aside-width-3';
		} else {
			this.asideClass = 'aside-content aside-width-10';
		}
	}

	// shortcuts to controls for readability
	get firstName(): AbstractControl {
		return this.dataForm.controls['firstName'];
	}
	get lastName(): AbstractControl {
		return this.dataForm.controls['lastName'];
	}
	get email(): AbstractControl {
		return this.dataForm.controls['email'];
	}
	get phoneNumber(): AbstractControl {
		return this.dataForm.controls['phoneNumber'];
	}
	get addressLine1(): AbstractControl {
		return this.dataForm.controls['addressLine1'];
	}
	get addressLine2(): AbstractControl {
		return this.dataForm.controls['addressLine2'];
	}
	get addressLine3(): AbstractControl {
		return this.dataForm.controls['addressLine3'];
	}
	get addressLine4(): AbstractControl {
		return this.dataForm.controls['addressLine4'];
	}
	get town(): AbstractControl {
		return this.dataForm.controls['town'];
	}
	get county(): AbstractControl {
		return this.dataForm.controls['county'];
	}
	get postcode(): AbstractControl {
		return this.dataForm.controls['postcode'];
	}
	get vehicleRegistrationNumber(): AbstractControl {
		return this.dataForm.controls['vehicleRegistrationNumber'];
	}
	get orbitContactPreference(): AbstractControl {
		return this.dataForm.controls['orbitContactPreference'];
	}
	get organiserContactPreference(): AbstractControl {
		return this.dataForm.controls['organiserContactPreference'];
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			firstName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(RegexValidationStrings.PERSON_NAME)]],
			lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(RegexValidationStrings.PERSON_NAME)]],
			email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(RegexValidationStrings.EMAIL)]],
			phoneNumber: ['', [Validators.required, Validators.maxLength(15), Validators.pattern(RegexValidationStrings.PHONE_NUMBER)]],
			addressLine1: ['', [Validators.required, Validators.maxLength(100)]],
			addressLine2: ['', [Validators.maxLength(100)]],
			addressLine3: ['', [Validators.maxLength(100)]],
			addressLine4: ['', [Validators.maxLength(100)]],
			town: ['', [Validators.required, Validators.maxLength(60)]],
			county: ['', [Validators.maxLength(25)]],
			postcode: ['', [Validators.required, Validators.maxLength(8), Validators.pattern(RegexValidationStrings.POSTCODE)]],
			vehicleRegistrationNumber: ['', [Validators.maxLength(8)]],
			orbitContactPreference: [''],
			organiserContactPreference: [''],
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.firstName.setValue(this.viewModel.firstName);
		this.lastName.setValue(this.viewModel.lastName);
		this.email.setValue(this.viewModel.email);
		this.phoneNumber.setValue(this.viewModel.phoneNumber);
		this.addressLine1.setValue(this.viewModel.addressLine1);
		this.addressLine2.setValue(this.viewModel.addressLine2);
		this.addressLine3.setValue(this.viewModel.addressLine3);
		this.addressLine4.setValue(this.viewModel.addressLine4);
		this.town.setValue(this.viewModel.town);
		this.county.setValue(this.viewModel.county);
		this.postcode.setValue(this.viewModel.postcode);
		this.vehicleRegistrationNumber.setValue(this.viewModel.vehicleRegistrationNumber);
		this.orbitContactPreference.setValue(this.viewModel.orbitContactPreference);
		this.organiserContactPreference.setValue(this.viewModel.organiserContactPreference);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<CustomerFormModel> {
		let dataModel: CustomerFormModel;

		await this.service.getAccountCustomerForm(id).then((response: CustomerFormModel) => {
			dataModel = response;
		});

		return Promise.resolve(dataModel);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createUpdateModel() {
		return {
			firstName: this.firstName.value,
			lastName: this.lastName.value,
			email: this.email.value,
			phoneNumber: this.phoneNumber.value,
			addressLine1: this.addressLine1.value,
			addressLine2: this.addressLine2.value,
			addressLine3: this.addressLine3.value,
			addressLine4: this.addressLine4.value,
			town: this.town.value,
			county: this.county.value,
			postcode: this.postcode.value,
			vehicleRegistrationNumber: this.vehicleRegistrationNumber.value,
			orbitContactPreference: this.orbitContactPreference.value,
			organiserContactPreference: this.organiserContactPreference.value,
			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.updateAccountCustomerForm(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async doCreate(dataToUpdate: any) {
		throw new Error('Method not implemented.');
	}
}
